import React, { useContext } from "react";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

import { toastOptions } from "../../../assets/constants";
import { AuthContext } from "../../../contexts";
import { CustomTag, CustomList, CustomPageHeader } from "../../../ui";

import pennylaneLogo from "../../../assets/icons/pennylane.svg";

const CustomersList = () => {
    const { token } = useContext(AuthContext);
    const [isSyncing, setIsSyncing] = React.useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [customers, setCustomers] = React.useState(null);
    const [refresh, setRefresh] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [filteredCustomers, setFilteredCustomers] = React.useState([]);

    React.useEffect(() => {
        setLoading(true);
        axios
            .get(process.env.REACT_APP_UPA_API_HOST + "pennylane/customers", {
                headers: { Authorization: "Bearer " + token },
            })
            .then((response) => {
                setLastUpdatedAt(response.data.length > 0 ? response.data[0].updatedAt : null);
                setCustomers(response.data);
                setFilteredCustomers(response.data);
            })
            .catch((error) => {
                console.log("Error fetching search data:", error);
            })
            .finally(() => setLoading(false));
    }, [token, refresh]);

    React.useEffect(() => {
        if (customers) {
            const term = searchTerm.toLowerCase();
            const results = customers.filter((customer) =>
                customer.name.toLowerCase().includes(term) ||
                (customer.reference && customer.reference.toLowerCase().includes(term))
            );
            setFilteredCustomers(results);
        }
    }, [searchTerm, customers]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const SupportHoursCell = ({ customer, supportInfo }) => {
        const hours = customer.supportHours || 0;

        return (
            <div className="conso-wrapper">
                <CustomTag
                    value={
                        supportInfo.isMutualized 
                            ? `${(supportInfo.groupInfo?.totalGroupHours || 0).toFixed(1)}h / ${supportInfo.groupInfo?.totalAvailableHours || supportInfo.premiumHours || 0}h`
                            : supportInfo.isPremium 
                                ? `${hours.toFixed(1)}h / ${supportInfo.premiumHours || 0}h`
                                : `${hours.toFixed(1)}h`
                    }
                    className={
                        supportInfo.isMutualized 
                            ? ((supportInfo.groupInfo?.totalGroupHours || 0) > (supportInfo.groupInfo?.totalAvailableHours || supportInfo.premiumHours || 0) ? "no-assistance" : "premium")
                            : supportInfo.isPremium 
                                ? (hours > (supportInfo.premiumHours || 0) ? "no-assistance" : "success")
                                : hours > 0 ? "no-assistance" : "success"
                    }
                />
            </div>
        );
    };

    const syncData = async () => {
        setIsSyncing(true);
        
        try {
            await toast.promise(
                axios.get(process.env.REACT_APP_UPA_API_HOST + "pennylane/sync-customers", {
                    headers: { Authorization: "Bearer " + token },
                }),
                {
                    pending: "Synchronisation des clients Pennylane en cours...",
                    success: "Synchronisation des clients Pennylane terminée",
                    error: "Erreur lors de la synchronisation Pennylane"
                },
                toastOptions
            );

            await toast.promise(
                axios.post(process.env.REACT_APP_UPA_API_HOST + "zammad/sync-customers", {}, {
                    headers: { Authorization: "Bearer " + token },
                }),
                {
                    pending: "Synchronisation des organisations Zammad en cours...",
                    success: (response) => {
                        const result = response.data?.data;
                        return `Synchronisation Zammad terminée : ${result?.matched || 0} correspondances, ${result?.created || 0} créations`;
                    },
                    error: "Erreur lors de la synchronisation Zammad"
                },
                toastOptions
            );

            setRefresh(!refresh);
            
        } catch (error) {
            console.error("Erreur lors de la synchronisation:", error);
        } finally {
            setIsSyncing(false);
        }
    };

    const customerCols = React.useMemo(
        () => [
            {
                Header: "ID",
                accessor: "reference",
                icon: "tag-simple",
                Cell: ({ cell: { value } }) => <CustomTag value={value || "000"} />,
            },
            {
                Header: "Nom",
                accessor: "name",
                icon: "user",
                defaultSortDesc: false,
                Cell: (row) => (
                    <div className="contact-list-customer-name">
                        <div className="contact-list-img">
                            <img src={row.row.original.logo ? row.row.original.logo : "https://image.store.altelis.com/images/altelis-tools/logo-altelis.png"} alt="contact-logo" />
                        </div>
                        <span>{row.row.original.name}</span>
                    </div>
                ),
            },
            {
                Header: "Support",
                accessor: "supportInfo",
                icon: "support",
                Cell: ({ cell: { value } }) => {
                    const getSupportDisplay = () => {
                        if (value.isMutualized) return "Mutualisé";
                        if (value.isPremium) return "Premium";
                        if (value.isEssential) return "Essentiel";
                        return "Aucun";
                    };
                    
                    const getSupportClass = () => {
                        if (value.isMutualized) return "premium";
                        if (value.isPremium) return "purple";
                        if (value.isEssential) return "blue";
                        return "deactivated";
                    };

                    return (
                        <CustomTag
                            value={getSupportDisplay()}
                            className={`uppercase ${getSupportClass()}`}
                        />
                    );
                },
            },
            {
                Header: "Conso.",
                icon: "conso",
                accessor: "supportHours",
                className: "col-conso",
                Cell: ({ row }) => {
                    const supportInfo = row.original.supportInfo;
                    const customer = row.original;

                    return <SupportHoursCell customer={customer} supportInfo={supportInfo} />;
                },
            },
            {
                Header: "MRR",
                icon: "euro",
                accessor: "mensualRateReturn",
                className: "col-conso",
                Cell: ({ cell: { value } }) => <CustomTag value={value.toFixed(2) + " €"} />,
            },
        ],
        []
    );

    return (
        <div className="c-app__view">
            <CustomPageHeader 
                title="Clients"
                titleIcon={{
                    svg: pennylaneLogo,
                    size: 30,
                }}
                lastUpdatedAt={lastUpdatedAt}
                buttons={[
                    {
                        label: isSyncing ? "Synchronisation en cours..." : "Synchroniser",
                        onClick: syncData,
                        icon: isSyncing ? null : ArrowsClockwiseIcon,
                        disabled: isSyncing,
                        loading: isSyncing,
                        variant: "primary"
                    }
                ]}
            />
            {!loading ? (
                <>
                    <div className="tabs-container">
                        <div className="input-container w-20">
                            <div className="single-search-input">
                                <input type="text" placeholder="Rechercher un client..." value={searchTerm} onChange={handleSearchChange} className="search-input" />
                            </div>
                        </div>
                        <CustomList
                            entity={filteredCustomers}
                            columns={customerCols}
                            isSelectable={false}
                            isScrollable={true}
                            limit={100}
                            entityType="customers"
                            initialSortBy={[{ id: "name", desc: false }]}
                        />
                    </div>
                </>
            ) : (
                <div className="loader-container absolute">
                    <CircularProgress size={30} />
                </div>
            )}
        </div>
    );
};

export default CustomersList;
